/**
 * Query keys — one factory per API family (UI overhaul PR-2, audit S2).
 *
 * A key names a piece of server data precisely enough that two components
 * asking for the same thing get the same cache entry, and precisely enough
 * that a different question never collides. Keys are plain JSON arrays:
 * TanStack hashes them deterministically (object key order does not matter),
 * so params can be passed as a single object.
 *
 * Every request also carries `X-Operator-Scope` (lib/operatorScope.ts), so
 * the same question under another operator's book is a different answer:
 * `useApiQuery` stores every entry under `scopedKey(key)` — the scope token
 * in front of the key below. Code that reads or writes the cache directly
 * (`setQueryData`, `getQueryData`) must use `scopedKey` too; invalidate by
 * prefix with `invalidatePrefix` (lib/query/invalidate.ts), which matches
 * the unscoped prefix in every scope.
 */
import { partialMatchKey, type QueryKey } from '@tanstack/react-query'
import { currentOperatorScope } from '../operatorScope'

export type Params = Record<string, unknown>

/** The cache key for `key` under the operator scope in force right now
 *  (or the `scope` given — components pass the reactive one, lib/query/scope.ts). */
export function scopedKey(key: QueryKey, scope: string = currentOperatorScope()): QueryKey {
  return [scope, ...key]
}

/** True when the scoped `full` key starts with the unscoped `prefix`. */
export function scopedKeyMatches(full: QueryKey, prefix: QueryKey): boolean {
  return partialMatchKey(full.slice(1), prefix)
}

export const keys = {
  /** GET /api/health — liveness probe (header control). */
  health: () => ['health'] as const,

  freshness: {
    /** GET /api/freshness — the ETL freshness observatory rollup. */
    overview: () => ['freshness', 'overview'] as const,
    /** GET /api/ppc-freshness — last complete PPC report day + sync state. */
    ppc: () => ['freshness', 'ppc'] as const,
  },

  admin: {
    /** GET /api/admin/refreshes — local-sync refresh jobs (System Health). */
    refreshes: () => ['admin', 'refreshes'] as const,
  },

  pnl: {
    /** Prefix for every P&L read — no single route. */
    all: () => ['pnl'] as const,
    /** GET /api/ops/products/pnl-coverage — per-marketplace settled-data horizon. */
    coverage: () => ['pnl', 'coverage'] as const,
    /** GET /api/ops/products/pnl-summary — one window, one marketplace set. */
    summary: (params: Params) => ['pnl', 'summary', params] as const,
    /** GET /api/ops/products/pnl-fee-history — one fee's history (the P&L fee drawer, #55). */
    feeHistory: (params: Params) => ['pnl', 'fee-history', params] as const,
  },

  marketplaceOverview: {
    /** GET /api/ops/marketplace-overview — every seller account's sales, ads and goal pace over the last N complete days (SellerMate W5.4). */
    overview: (params: Params) => ['marketplace-overview', params] as const,
  },

  recs: {
    /** Prefix for every Suggestions read — no single route. */
    all: () => ['recs'] as const,
    /** GET /api/recs?envelope=1&… — the Suggestions table page. */
    paged: (params: Params) => ['recs', 'paged', params] as const,
    /** GET /api/recs-campaign-rail — the Suggestions page's campaign rail (W4.1). */
    rail: (params: Params) => ['recs', 'rail', params] as const,
    /** GET /api/recs — the Command Center chip's whole-list read. */
    chip: () => ['recs', 'chip'] as const,
    /** GET /api/recs/for-rows — the open suggestions for one page of
     *  Advertising hub rows (the "ClickPay suggests" chips, PR 4 #70). Under
     *  the `recs` prefix, so every stage that invalidates `recs.all()`
     *  refreshes the chips too. */
    forRows: (params: Params) => ['recs', 'for-rows', params] as const,
  },

  /** PR 4 — the Advertising hub (/ads; backend/api/ads_hub.py). */
  ads: {
    /** Prefix for every hub read — no single route. */
    all: () => ['ads'] as const,
    /** GET /api/ads/campaign-workspace — one campaign's header facts and its
     *  three placement rows (the campaign workspace, H2). */
    workspace: (params: Params) => ['ads', 'workspace', params] as const,
    /** GET /api/ads/scope-labels — names for the ids a hub URL carries (the
     *  breadcrumb and the bottom-tab title). */
    scopeLabels: (params: Params) => ['ads', 'scope-labels', params] as const,
  },

  commandCenter: {
    /** Prefix for every Command Center read — no single route. */
    all: () => ['command-center'] as const,
    /** GET /api/dashboard/kpis */
    kpis: (days: number) => ['command-center', 'kpis', days] as const,
    /** GET /api/dashboard/spend-trend */
    spendTrend: (days: number) => ['command-center', 'spend-trend', days] as const,
    /** GET /api/dashboard/acos-trend */
    acosTrend: (days: number) => ['command-center', 'acos-trend', days] as const,
    /** GET /api/dashboard/top-campaigns */
    topCampaigns: (days: number, n: number) => ['command-center', 'top-campaigns', days, n] as const,
    /** GET /api/dashboard/recent-fires */
    recentFires: (hours: number) => ['command-center', 'recent-fires', hours] as const,
    /** GET /api/approvals */
    pendingApprovals: () => ['command-center', 'pending-approvals'] as const,
    /** GET /api/rules */
    rules: () => ['command-center', 'rules'] as const,
    /** GET /api/dashboard/tacos-live */
    tacosLive: () => ['command-center', 'tacos-live'] as const,
    /** GET /api/ops/triage-summary */
    triage: () => ['command-center', 'triage'] as const,
  },

  ppc: {
    /** Prefix for every PPC read — no single route. */
    all: () => ['ppc'] as const,
    /** GET /api/campaigns */
    campaigns: (params: Params) => ['ppc', 'campaigns', params] as const,
    /** GET /api/ad-groups */
    adGroups: (params: Params) => ['ppc', 'ad-groups', params] as const,
    /** GET /api/keywords */
    keywords: (params: Params) => ['ppc', 'keywords', params] as const,
    /** GET /api/placements */
    placements: (params: Params) => ['ppc', 'placements', params] as const,
    /** GET /api/product-ads */
    productAds: (params: Params) => ['ppc', 'product-ads', params] as const,
    /** Prefix for every products read (invalidation target) — no single route. */
    productsAll: () => ['ppc', 'products'] as const,
    /** GET /api/products */
    products: (params: Params) => ['ppc', 'products', params] as const,
    /** GET /api/search-terms */
    searchTerms: (params: Params) => ['ppc', 'search-terms', params] as const,
    /** GET /api/targets */
    targets: (params: Params) => ['ppc', 'targets', params] as const,
    /** GET /api/ads/other-items - the "What else they bought" drawer for one
     *  keyword / target (feedback #121). */
    otherItems: (params: Params) => ['ppc', 'other-items', params] as const,
    /** GET /api/targets-all — the "All" targets tab: keywords ∪ targets as
     *  one server-paged list (PR 3, G8; it replaced two 50k-capped reads). */
    targetsAll: (params: Params) => ['ppc', 'targets-all', params] as const,
    /** Prefix for every /api/targets-all read. Its Tag select is answered by
     *  the SERVER, so a tag write changes which rows match: invalidated after
     *  one (`useUserTags().refresh`). */
    targetsAllAll: () => ['ppc', 'targets-all'] as const,
    /** GET /api/budget-pacing */
    budgetPacing: () => ['ppc', 'budget-pacing'] as const,
    /** GET /api/timeseries/ams-today-by-asin */
    amsTodaySpend: () => ['ppc', 'ams-today-spend'] as const,
    /** GET /api/products-coverage — the Coverage view / product strip. */
    coverage: (params: Params) => ['ppc', 'coverage', params] as const,
    /** GET /api/campaigns/today-live */
    campaignsTodayLive: () => ['ppc', 'campaigns-today-live'] as const,
    /** GET /api/hourly/sp-keyword */
    hourlyKeywords: (params: Params) => ['ppc', 'hourly-keywords', params] as const,
    /** GET /api/hourly/ppc */
    hourlyPpc: (params: Params) => ['ppc', 'hourly-ppc', params] as const,
    /** GET /api/hourly/heatmap (SM W5.1) */
    hourlyHeatmap: (params: Params) => ['ppc', 'hourly-heatmap', params] as const,
    /** GET /api/timeseries merged with /api/timeseries/ams-tail — one chart
     *  series for {source, window, filters, breakout} (PerfSummary). */
    timeseries: (params: Params) => ['ppc', 'timeseries', params] as const,
    /** GET /api/timeseries + /api/timeseries/ams-tail summed into one period
     *  tile's totals (PpcPeriodTiles). */
    periodTotals: (params: Params) => ['ppc', 'period-totals', params] as const,
    /** GET /api/campaigns/placement-shares — TOS / PP / RoS spend share per campaign. */
    placementShares: (params: Params) => ['ppc', 'placement-shares', params] as const,
    /** GET /api/bulk/campaign-placements — up to 200 campaigns' CURRENT
     *  placement adjustments, read when a bulk placement edit opens (SM W1.3). */
    campaignPlacementModifiers: (campaignIds: readonly string[]) => ['ppc', 'campaign-placement-modifiers', [...campaignIds]] as const,
  },

  /** SellerMate W1.7 — the Negatives page and saved negative lists
   *  (backend/api/negatives_api.py). */
  negatives: {
    /** Prefix for every Negatives read — no single route. Invalidated after a
     *  list is saved, deleted or queued. */
    all: () => ['negatives'] as const,
    /** GET /api/negatives/keywords — one server page of the synced negative keywords. */
    keywords: (params: Params) => ['negatives', 'keywords', params] as const,
    /** GET /api/negatives/products — one server page of the synced negative products. */
    products: (params: Params) => ['negatives', 'products', params] as const,
    /** GET /api/negatives/lists — every saved negative list with its size. */
    lists: () => ['negatives', 'lists'] as const,
    /** GET /api/negatives/lists/{list_id} — one saved list with its items. */
    list: (id: number) => ['negatives', 'list', id] as const,
    /** GET /api/negatives/destinations — the "Add list to…" campaign / ad-group picker. */
    destinations: (level: string, search: string) => ['negatives', 'destinations', level, search] as const,
  },

  /** GET /api/profiles — the Ads profiles (marketplaces) roster. */
  profiles: () => ['profiles'] as const,

  /** GET /api/campaign-builder/options — the campaign builder's pickers
   *  (marketplaces, one marketplace's portfolios, limits). */
  campaignBuilderOptions: (profileId: string | null) => ['campaign-builder', 'options', profileId] as const,
  /** GET /api/campaign-builder/options asked about specific ASINs: their SKUs and next single-keyword number. */
  campaignBuilderSkus: (profileId: string | null, asins: string) => ['campaign-builder', 'skus', profileId, asins] as const,
  harvest: {
    /** GET /api/search-terms/harvest/options — the harvest dialog's tiers, SKUs
     *  and source-campaign facts for the selected terms. */
    options: (params: Params) => ['harvest', 'options', params] as const,
    /** GET /api/search-terms/harvest/destinations — one product's manual campaigns. */
    destinations: (params: Params) => ['harvest', 'destinations', params] as const,
    /** GET /api/manual-edits/live-writes-status — what the dialog's gate line reads. */
    writes: () => ['harvest', 'writes'] as const,
    /** GET /api/manual-edits/apply-new-campaigns/{job_id} — the tray's "Create all" run being watched. */
    newCampaignJob: (jobId: string | null) => ['harvest', 'new-campaign-job', jobId] as const,
  },
  /** POST /api/campaign-builder/preview for a House launch (PR 17): the server
   *  plans it, so the build step reads the plan for the config as typed. */
  campaignBuilderPreview: (configJson: string) => ['campaign-builder', 'preview', configJson] as const,

  /** GET /api/manual-edits — the manual-edit tray (pending + recent). */
  pendingEdits: (params: Params) => ['pending-edits', params] as const,
  /** Prefix for every pendingEdits key — what to invalidate when a manual edit
   *  is applied, discarded or requeued from anywhere. Invalidation prefix, so
   *  no single route of its own; the keys beneath it read GET /api/manual-edits. */
  pendingEditsAll: () => ['pending-edits'] as const,

  /** ClickPay Suggests' own queue (PR-B): pending manual edits noted with the
   *  Suggests prefix, and the live-writes posture its Approve-all confirm reads. */
  suggestsQueue: {
    /** Prefix for every suggestsQueue key — invalidated after an apply,
     *  discard or requeue. Invalidation prefix, so no single route of its own. */
    all: () => ['suggests-queue'] as const,
    /** GET /api/manual-edits — pending rows carrying the Suggests note prefix
     *  (status=pending, scope=all, notes_prefix). The Queued tab's main list. */
    list: () => ['suggests-queue', 'list'] as const,
    /** GET /api/manual-edits — recent FAILED Suggests rows (status=failed).
     *  They are in no 'pending' list, so without this read they show nowhere. */
    failed: () => ['suggests-queue', 'failed'] as const,
    /** GET /api/manual-edits/live-writes-status — the write posture the
     *  Approve-all confirm reads before it offers to send anything. */
    liveWrites: () => ['suggests-queue', 'live-writes'] as const,
  },

  search: {
    /** GET /api/search/identifiers — every ASIN's UIDs and SKUs, the aliases a
     *  browser-side search box matches a row's ASIN through. */
    identifiers: () => ['search', 'identifiers'] as const,
    /** GET /api/search — Ctrl+K palette: records matching one query, grouped
     *  by kind (backend/api/command_search.py). */
    palette: (q: string) => ['search', 'palette', q] as const,
  },

  goals: {
    /** GET /api/goals — per-ASIN goal presets (Products page overlay). */
    all: () => ['goals'] as const,
    /** GET /api/goals/account-target - the one account ACoS target (PR 12, D6). */
    accountTarget: () => ['goals', 'account-target'] as const,
  },
  /** GET /api/ops/product-managers — who owns which ASIN. */
  productManagers: () => ['product-managers'] as const,
  /** GET /api/operators — the operator roster. */
  operators: () => ['operators'] as const,
  tacos: {
    /** GET /api/tacos/bulk — 30-day TACoS per uid for one marketplace. */
    bulk: (marketplace: string, days: number) => ['tacos', 'bulk', marketplace, days] as const,
  },
  /** GET /api/ops/products/group-keys — variation / pack families for grouping. */
  productGroupKeys: () => ['products', 'group-keys'] as const,

  products: {
    /** Prefix for every product-snapshot read (PR 10) — no single route. */
    all: () => ['products', 'snapshot'] as const,
    /** GET /api/ops/products/snapshot — every product's stock, 30-day money
     *  and listing status for one scope ('all' or a hub marketplace). */
    snapshot: (marketplace: string) => ['products', 'snapshot', 'list', marketplace] as const,
    /** GET /api/ops/products/{uid}/quick-view — the quick-view drawer. */
    quickView: (uid: string, marketplace: string) => ['products', 'snapshot', 'quick-view', uid, marketplace] as const,
    /** GET /api/ops/products/unlinked-sales — selling, not in the catalog. */
    unlinkedSales: (days: number) => ['products', 'snapshot', 'unlinked-sales', days] as const,
  },

  productSettings: {
    /** Prefix for every Product settings read — no single route. Invalidate
     *  this after a hide/unhide so the list, the counts and the open product
     *  all re-read. */
    all: () => ['product-settings'] as const,
    /** GET /api/ops/product-settings — every product, hidden ones included. */
    list: () => ['product-settings', 'list'] as const,
    /** GET /api/ops/product-settings/summary — hidden-product counts only. */
    summary: () => ['product-settings', 'summary'] as const,
    /** GET /api/ops/product-settings/detail — one product's full settings. */
    detail: (uid: string) => ['product-settings', 'detail', uid] as const,
    /** GET /api/ops/products/{uid}/components — "What's in one pack" (PR 11). */
    recipe: (uid: string) => ['product-settings', 'recipe', uid] as const,
    /** GET /api/ops/products/component-catalog — every recipe component. */
    componentCatalog: () => ['product-settings', 'component-catalog'] as const,
    /** GET /api/ops/supplier-reorder/{uid}/trend-multiplier — this product's
     *  own trend multiplier and its If-Match version (PR 11, #94). */
    trendMultiplier: (uid: string) => ['product-settings', 'trend-multiplier', uid] as const,
  },

  ops: {
    /** GET /api/ops/qbo/status */
    qboStatus: () => ['ops', 'qbo-status'] as const,
    /** GET /api/ops/qbwc/status */
    qbdStatus: () => ['ops', 'qbd-status'] as const,
    /** GET /api/ops/sync/status — per-source last-run entries (System Health). */
    syncStatus: () => ['ops', 'sync-status'] as const,
    /** GET /api/ops/receive-queue/count */
    receiveQueueCount: () => ['ops', 'receive-queue-count'] as const,
    /** GET /api/agent-sessions */
    sessions: () => ['ops', 'agent-sessions'] as const,
    /** GET /api/ops/vessels — active shipments with a vessel (Shipments › Map). */
    vessels: () => ['ops', 'vessels'] as const,
    /** GET /api/ops/vessels/{shipping_number} — one shipment's vessel (the drawer). */
    vessel: (ship: string) => ['ops', 'vessel', ship] as const,
    /** GET /api/ops/vessels/health — live AIS tracking status (the map header). */
    vesselHealth: () => ['ops', 'vessel-health'] as const,
  },

  changeHistory: {
    /** Prefix for every Change-history read — no single route. */
    all: () => ['change-history'] as const,
    /** GET /api/audit/history — one filtered page of the ClickPay change trail
     *  (SellerMate W1.11; backend/api/audit_history.py). */
    page: (params: Params) => ['change-history', 'page', params] as const,
    /** GET /api/audit/history/facets — the values each filter dropdown offers. */
    facets: () => ['change-history', 'facets'] as const,
    /** GET /api/audit/amazon — the Amazon side of the change trail, paged. */
    amazon: (params: Params) => ['change-history', 'amazon', params] as const,
  },

  /** SellerMate W1.10 — Product DNA, the PPC product page's reads
   *  (pages/ProductDetail.tsx, components/productDna/, lib/productDna.ts). */
  productDna: {
    /** Prefix for every Product DNA read — no single route. */
    all: () => ['product-dna'] as const,
    /** GET /api/product-ads-rollup — one ASIN's Sponsored Products ads with
     *  their campaigns and ad groups: the Performance tab's funnel and the
     *  Campaigns tab's by-campaign view share it for the same dates. */
    rollup: (asin: string, from: string, to: string) => ['product-dna', 'rollup', asin, from, to] as const,
    /** GET /api/products/{asin}/dna/targeting — spend and results by match
     *  type / targeting type across the campaigns advertising the ASIN. */
    targeting: (asin: string, from: string, to: string) => ['product-dna', 'targeting', asin, from, to] as const,
    /** GET /api/products/{asin}/dna/co-advertised — every product advertised
     *  in those campaigns (the Campaigns tab's by-product view). */
    coAdvertised: (asin: string, from: string, to: string) => ['product-dna', 'co-advertised', asin, from, to] as const,
    /** GET /api/tacos/{uid} — the product's TACoS rows per marketplace and
     *  window (the organic-vs-ad donut). */
    tacos: (uid: string | null) => ['product-dna', 'tacos', uid] as const,
    /** GET /api/ops/restock-hub/{uid}/inventory-daily — the Restock Hub's
     *  trusted daily stock series (the Inventory tab and Overview card). */
    inventoryDaily: (uid: string | null, market: string, days: number) => ['product-dna', 'inventory-daily', uid, market, days] as const,
  },

  /** PR 14 — what changed on a product (backend/api/product_events_api.py):
   *  the chart markers and the product page's Events table. */
  productEvents: {
    /** Prefix for every product-events read — no single route. Invalidated
     *  after a note is added or removed. */
    all: () => ['product-events'] as const,
    /** GET /api/product-events — events in a date range (chart markers). */
    list: (params: Params) => ['product-events', 'list', params] as const,
    /** GET /api/product-events/effects — one product's events with their
     *  before/after cards (the Performance tab's markers AND Events table). */
    effects: (params: Params) => ['product-events', 'effects', params] as const,
    /** GET /api/product-events/price-history — Keepa's daily price series
     *  for one ASIN (PR 22, the Performance tab's price chart). */
    priceHistory: (asin: string, from: string, to: string) =>
      ['product-events', 'price-history', asin, from, to] as const,
  },

  /** PR 15 — the Deals page (backend/api/deals_api.py). */
  deals: {
    /** Prefix for every Deals read — no single route. Invalidated after
     *  Check Amazon access and Sync now. */
    all: () => ['deals'] as const,
    /** GET /api/deals — deals and coupons with their results. */
    list: (params: Params) => ['deals', 'list', params] as const,
    /** GET /api/deals/status — where the deal data comes from, connected or not. */
    status: () => ['deals', 'status'] as const,
    /** GET /api/deals/recommendations — "ClickPay suggests". */
    recommendations: (params: Params) => ['deals', 'recommendations', params] as const,
  },

  /** SellerMate W5.2 — the Ads Overview report (backend/api/ads_overview.py). */
  adsOverview: {
    /** Prefix for every Ads Overview read — no single route. Invalidated
     *  after a user-tag write, since the Tag breakdown reads the tags. */
    all: () => ['ads-overview'] as const,
    /** GET /api/reports/ads-overview */
    overview: (params: Params) => ['ads-overview', 'overview', params] as const,
    /** GET /api/reports/ads-overview/breakdown */
    breakdown: (params: Params) => ['ads-overview', 'breakdown', params] as const,
    /** GET /api/reports/ads-overview/trends */
    trends: (params: Params) => ['ads-overview', 'trends', params] as const,
  },

  /** SellerMate W5.3 — a person's own tags on rows (backend/api/user_tags.py). */
  userTags: {
    /** Prefix for every user-tag read — no single route. Invalidated after
     *  any tag write so every table's chips and filters re-read. */
    all: () => ['userTags'] as const,
    /** GET /api/user-tags — every tag with per-entity-type counts. */
    catalog: () => ['userTags', 'catalog'] as const,
    /** GET /api/user-tags/assignments — entity_id → tag ids for one entity type. */
    assignments: (entityType: string) => ['userTags', 'assignments', entityType] as const,
  },

  ui: {
    /** NOT a request — there is no GET route behind this key. It is the
     *  shared visibility-gated clock in lib/query/liveTick.ts, whose
     *  "answer" IS the tick, so nothing is fetched and nothing needs a
     *  budget. Keyed by cadence so every pane asking for the same cadence
     *  shares one timer. */
    liveTick: (cadenceMs: number) => ['ui', 'live-tick', cadenceMs] as const,
  },

  amc: {
    /** POST /api/amc/poll — a write the panels re-read after; no GET route. */
    poll: () => ['amc', 'poll'] as const,
    /** GET /api/amc/insights */
    insights: () => ['amc', 'insights'] as const,
    /** GET /api/amc/insights/templates */
    insightTemplates: () => ['amc', 'insight-templates'] as const,
    /** GET /api/amc/audiences */
    audiences: () => ['amc', 'audiences'] as const,
  },

  alertsV2: {
    /** Prefix for every Alerts v2 read — no single route. Invalidate this
     *  after any alert write (save, delete, ack, snooze). */
    all: () => ['alerts-v2'] as const,
    /** GET /api/alerts-v2/templates — the alert template catalogue. */
    templates: () => ['alerts-v2', 'templates'] as const,
    /** GET /api/alerts-v2/defs — every saved alert. */
    defs: () => ['alerts-v2', 'defs'] as const,
    /** GET /api/alerts-v2/events — the alert log (status / search / window). */
    eventsLog: (params: Params) => ['alerts-v2', 'events', 'log', params] as const,
    /** GET /api/alerts-v2/events — every active (open + snoozed) event. */
    eventsActive: () => ['alerts-v2', 'events', 'active'] as const,
  },

  /** PR 8 — the one header bell and the System notifications tab
   *  (backend/api/notifications_api.py). */
  notifications: {
    /** Prefix for every notifications read — no single route. Invalidated
     *  after mark-read, a preferences save or a resolve. */
    all: () => ['notifications'] as const,
    /** GET /api/notifications — the bell: For you / Data / System, unread count. */
    feed: () => ['notifications', 'feed'] as const,
    /** GET /api/notifications/preferences — what reaches my bell / my email. */
    prefs: () => ['notifications', 'prefs'] as const,
    /** GET /api/notifications/incidents — admin: one row per incident (status / source / retired). */
    incidents: (params: Params) => ['notifications', 'incidents', params] as const,
  },

  chat: {
    /** GET /api/chat/conversations — your AI Assistant threads, newest first,
     *  with `has_more` when older ones exist (SM W6.1). */
    conversations: () => ['chat', 'conversations'] as const,
    /** GET /api/chat/conversations/{conversation_id} — one thread with every message. */
    conversation: (id: number | null) => ['chat', 'conversation', id] as const,
  },

  ai: {
    /** GET /api/ai/prompts — the AI prompt library, grouped by what you want
     *  to do (SM W6.3). */
    library: () => ['ai', 'prompts'] as const,
    /** GET /api/ai/scheduled-prompts — scheduled briefings with their next
     *  run, plus the unread count. */
    schedules: () => ['ai', 'scheduled-prompts'] as const,
    /** GET /api/ai/briefings — delivered briefings, newest first. */
    briefings: () => ['ai', 'briefings'] as const,
  },

  /** PR 17 — the new-product launch flow (backend/api/launch_api.py), the
   *  Launch tab on both product pages and the Pending changes tray's
   *  "are listing updates switched on" read. */
  launch: {
    /** Prefix for every launch read — no single route. */
    all: () => ['launch'] as const,
    /** GET /api/launches/status — what this server can do for a launch. */
    status: () => ['launch', 'status'] as const,
    /** GET /api/launches/by-uid/{uid} — the whole Launch tab for a product. */
    byUid: (uid: string) => ['launch', 'by-uid', uid] as const,
    /** GET /api/launches/{launch_id} — one launch by id (the New campaign page's link back). */
    one: (id: number) => ['launch', 'one', id] as const,
    /** GET /api/launches/{launch_id}/keywords — the seed list and the primary 10. */
    keywords: (id: number) => ['launch', 'keywords', id] as const,
    /** GET /api/launches/{launch_id}/keywords/competitors — likely competitor ASINs. */
    competitors: (id: number) => ['launch', 'competitors', id] as const,
    /** GET /api/launches/{launch_id}/listing — the draft, siblings and product types. */
    listing: (id: number) => ['launch', 'listing', id] as const,
    /** GET /api/launches/{launch_id}/ppc/preset — the House launch starting config. */
    preset: (id: number) => ['launch', 'preset', id] as const,
  },

  /** PR 16 — the Image studio (backend/api/media_api.py), the product page's
   *  Images tab. */
  media: {
    /** Prefix for every Image studio read — no single route. */
    all: () => ['media'] as const,
    /** GET /api/media/status — engine switches, the Pixii credit meter, disk. */
    status: () => ['media', 'status'] as const,
    /** GET /api/media/products/{uid}/studio — everything the tab shows. */
    studio: (uid: string) => ['media', 'studio', uid] as const,
    /** GET /api/media/jobs/{job_id} — one job and its log (the Details drawer). */
    job: (id: number) => ['media', 'job', id] as const,
    /** GET /api/products?search=<asin> — is a typed ASIN one of ours (the
     *  "copy another listing" warning). */
    asinIsOurs: (asin: string) => ['media', 'asin-is-ours', asin] as const,
    /** GET /api/products?search=… — the Clone dialog's product picker. */
    productSearch: (q: string) => ['media', 'product-search', q] as const,
  },
} as const
